/**
 * Unified Tracking System - Client-Side
 *
 * Central tracking function that sends events to all configured platforms.
 * Respects user consent and handles errors gracefully.
 */

'use client';

import type {
  TrackingEvent,
  TrackingIdentity,
  TrackingConfig,
  TrackingAdapter,
  TrackingResult,
} from './types';
import { hasConsent } from './consent';
import { KlaviyoAdapter } from './adapters/klaviyo';
import { GTMAdapter } from './adapters/gtm';
import { MetaAdapter } from './adapters/meta';
import { ShopifyAdapter } from './adapters/shopify';
import { getAnonymousId } from '@/lib/klaviyo/utils';

/**
 * Default tracking configuration
 */
const DEFAULT_CONFIG: TrackingConfig = {
  debug: process.env.NODE_ENV === 'development',
  environment: (process.env.NODE_ENV as any) || 'development',
  platforms: {
    klaviyo: {
      enabled: true,
      requiresConsent: 'marketing',
      clientSide: true,
      serverSide: true,
    },
    gtm: {
      enabled: true,
      requiresConsent: 'analytics',
      clientSide: true,
      serverSide: true,
    },
    meta: {
      enabled: false, // Enable when ready
      requiresConsent: 'marketing',
      clientSide: true,
      serverSide: true,
    },
    shopify: {
      enabled: false, // Enable when Shopify is integrated
      requiresConsent: 'necessary',
      clientSide: false,
      serverSide: true,
    },
  },
};

/**
 * Tracking configuration (can be overridden)
 */
let config: TrackingConfig = DEFAULT_CONFIG;

/**
 * Set tracking configuration
 */
export function setTrackingConfig(newConfig: Partial<TrackingConfig>): void {
  config = {
    ...config,
    ...newConfig,
    platforms: {
      ...config.platforms,
      ...newConfig.platforms,
    },
  };

  if (config.debug) {
    console.log('⚙️ Tracking config updated:', config);
  }
}

/**
 * Get current tracking configuration
 */
export function getTrackingConfig(): TrackingConfig {
  return config;
}

/**
 * Initialize all tracking adapters
 */
function getAdapters(): TrackingAdapter[] {
  const adapters: TrackingAdapter[] = [];

  if (
    config.platforms.klaviyo?.enabled &&
    config.platforms.klaviyo?.clientSide
  ) {
    adapters.push(new KlaviyoAdapter());
  }

  if (config.platforms.gtm?.enabled && config.platforms.gtm?.clientSide) {
    adapters.push(new GTMAdapter());
  }

  if (config.platforms.meta?.enabled && config.platforms.meta?.clientSide) {
    adapters.push(new MetaAdapter());
  }

  if (
    config.platforms.shopify?.enabled &&
    config.platforms.shopify?.clientSide
  ) {
    adapters.push(new ShopifyAdapter());
  }

  if (config.debug && adapters.length > 0) {
    console.log(
      '🔌 Initialized adapters:',
      adapters.map((a) => a.name).join(', '),
    );
  }

  return adapters;
}

/**
 * Track an event across all enabled platforms
 *
 * @example
 * ```ts
 * trackEvent({
 *   eventName: 'button_clicked',
 *   properties: { button_name: 'Sign Up' },
 * });
 * ```
 */
export async function trackEvent(
  event: TrackingEvent,
): Promise<TrackingResult[]> {
  const results: TrackingResult[] = [];

  // Enrich event with context
  const enrichedEvent = enrichEvent(event);

  if (config.debug) {
    console.log('🎯 Tracking event:', enrichedEvent);
  }

  const adapters = getAdapters();

  // Track to all adapters in parallel
  const promises = adapters.map(async (adapter): Promise<TrackingResult> => {
    try {
      // Check if adapter is available
      if (!adapter.isAvailable()) {
        if (config.debug) {
          console.warn(`⚠️ ${adapter.name} adapter not available`);
        }
        return {
          success: false,
          platform: adapter.name,
          error: 'Adapter not available',
        };
      }

      // Check consent
      const platformConfig =
        config.platforms[adapter.name as keyof typeof config.platforms];
      if (platformConfig?.requiresConsent) {
        if (!hasConsent(platformConfig.requiresConsent)) {
          if (config.debug) {
            console.log(`⛔ ${adapter.name} tracking blocked by consent`);
          }
          return {
            success: false,
            platform: adapter.name,
            error: 'Consent not granted',
          };
        }
      }

      // Track the event
      await adapter.track(enrichedEvent);

      if (config.debug) {
        console.log(`✅ ${adapter.name} tracked successfully`);
      }

      return {
        success: true,
        platform: adapter.name,
      };
    } catch (error) {
      console.error(`❌ ${adapter.name} tracking failed:`, error);
      return {
        success: false,
        platform: adapter.name,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  });

  // Wait for all tracking to complete
  const settled = await Promise.allSettled(promises);

  settled.forEach((result) => {
    if (result.status === 'fulfilled') {
      results.push(result.value);
    } else {
      results.push({
        success: false,
        platform: 'unknown',
        error: result.reason?.message || 'Promise rejected',
      });
    }
  });

  return results;
}

/**
 * Identify a user across all platforms
 *
 * @example
 * ```ts
 * identifyUser({
 *   email: 'user@example.com',
 *   firstName: 'John',
 *   lastName: 'Doe',
 * });
 * ```
 */
export async function identifyUser(
  identity: TrackingIdentity,
): Promise<TrackingResult[]> {
  const results: TrackingResult[] = [];

  // Add anonymous ID if available
  const enrichedIdentity: TrackingIdentity = {
    ...identity,
    anonymousId: identity.anonymousId || getAnonymousId() || undefined,
  };

  if (config.debug) {
    console.log('👤 Identifying user:', enrichedIdentity);
  }

  const adapters = getAdapters();

  // Identify on all adapters in parallel
  const promises = adapters.map(async (adapter): Promise<TrackingResult> => {
    try {
      if (!adapter.isAvailable()) {
        return {
          success: false,
          platform: adapter.name,
          error: 'Adapter not available',
        };
      }

      // Check consent
      const platformConfig =
        config.platforms[adapter.name as keyof typeof config.platforms];
      if (platformConfig?.requiresConsent) {
        if (!hasConsent(platformConfig.requiresConsent)) {
          return {
            success: false,
            platform: adapter.name,
            error: 'Consent not granted',
          };
        }
      }

      await adapter.identify(enrichedIdentity);

      if (config.debug) {
        console.log(`✅ ${adapter.name} identified successfully`);
      }

      return {
        success: true,
        platform: adapter.name,
      };
    } catch (error) {
      console.error(`❌ ${adapter.name} identify failed:`, error);
      return {
        success: false,
        platform: adapter.name,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  });

  const settled = await Promise.allSettled(promises);

  settled.forEach((result) => {
    if (result.status === 'fulfilled') {
      results.push(result.value);
    }
  });

  return results;
}

/**
 * Track a page view
 *
 * @example
 * ```ts
 * trackPageView({ pageTitle: 'Home' });
 * ```
 */
export async function trackPageView(
  properties?: Record<string, unknown>,
): Promise<TrackingResult[]> {
  if (config.debug) {
    console.log(
      '📄 Tracking page view:',
      typeof window !== 'undefined' ? window.location.href : 'SSR',
    );
  }

  return trackEvent({
    eventName: 'page_view',
    properties: {
      ...properties,
      page_url:
        typeof window !== 'undefined' ? window.location.href : undefined,
      page_title: typeof document !== 'undefined' ? document.title : undefined,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    },
  });
}

/**
 * Enrich event with automatic context data
 */
function enrichEvent(event: TrackingEvent): TrackingEvent {
  const enriched: TrackingEvent = { ...event };

  // Add event ID if not present (for deduplication across platforms)
  if (!enriched.eventId) {
    enriched.eventId = generateEventId();
    if (config.debug) {
      console.log('🆔 Generated event ID:', enriched.eventId);
    }
  }

  // Add URL if not present
  if (!enriched.url && typeof window !== 'undefined') {
    enriched.url = window.location.href;
  }

  // Add page title if not present
  if (!enriched.pageTitle && typeof document !== 'undefined') {
    enriched.pageTitle = document.title;
  }

  // Add timestamp if not present
  if (!enriched.timestamp) {
    enriched.timestamp = new Date().toISOString();
  }

  // Add anonymous ID to identity if available
  if (!enriched.identity?.anonymousId) {
    const anonymousId = getAnonymousId();
    if (anonymousId) {
      enriched.identity = {
        ...enriched.identity,
        anonymousId,
      };
    }
  }

  return enriched;
}

/**
 * Generate unique event ID for deduplication
 */
function generateEventId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Enable debug mode
 */
export function enableDebug(): void {
  config.debug = true;
  console.log('🐛 Debug mode ENABLED for tracking system');
}

/**
 * Disable debug mode
 */
export function disableDebug(): void {
  console.log('🐛 Debug mode DISABLED for tracking system');
  config.debug = false;
}
